import {
  Box,
  Container,
  Heading,
  Text,
  Badge,
  useColorModeValue,
  Flex,
  VStack,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import { FaCodeBranch } from "react-icons/fa";
import t from "../translations";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

/* ───────────────────────── small helpers ───────────────────────── */

function CommitDot({ color, size = "16px", glow = false }) {
  const bg = useColorModeValue(`${color}.500`, `${color}.400`);
  return (
    <Box
      w={size}
      h={size}
      minW={size}
      minH={size}
      borderRadius="full"
      bg={bg}
      boxShadow={glow ? `0 0 10px 2px var(--chakra-colors-${color}-400)` : undefined}
      zIndex={3}
      position="relative"
    >
      {/* inner ring */}
      <Box
        position="absolute"
        inset="-4px"
        borderRadius="full"
        border="2px solid"
        borderColor={useColorModeValue(`${color}.200`, `${color}.800`)}
        opacity={0.5}
      />
    </Box>
  );
}

/* Vertical line segment — rendered above or below a node */
function LineSeg({ color, position = "above", height = "100%" }) {
  const bg = useColorModeValue(`${color}.300`, `${color}.700`);
  return (
    <Box
      w="2px"
      bg={bg}
      flexGrow={position === "fill" ? 1 : 0}
      h={position === "fill" ? undefined : height}
      mx="auto"
      borderRadius="full"
    />
  );
}

/* ─────────────────────── Branch card ──────────────────────────── */

function BranchCard({ item, isLeft, color, isFirst, isLast }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  const cardBg = useColorModeValue("white", "rgba(26, 32, 44, 0.85)");
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const branchColor = useColorModeValue(`${item.color}.400`, `${item.color}.500`);
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const dateBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const topAccent = useColorModeValue(`${item.color}.400`, `${item.color}.400`);

  return (
    <MotionBox
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, ease: "easeOut" }}
      w="100%"
    >
      {/* Desktop */}
      <Flex display={{ base: "none", md: "flex" }} align="stretch" w="100%">
        {/* Left column */}
        <Box flex="1" display="flex" alignItems="center" justifyContent="flex-end" pr={0}>
          {isLeft ? (
            <CardContent
              item={item}
              mutedText={mutedText}
              cardBg={cardBg}
              cardBorder={cardBorder}
              topAccent={topAccent}
              dateBg={dateBg}
              align="right"
            />
          ) : (
            <Text
              fontSize="sm"
              fontWeight={600}
              color={mutedText}
              fontFamily="mono"
              pr={4}
            >
              {item.date}
            </Text>
          )}
        </Box>

        {/* Center spine: line → dot → line */}
        <Flex direction="column" align="center" w="50px" flexShrink={0}>
          {!isFirst && <LineSeg color={color} position="fill" />}
          {/* horizontal connector */}
          <Box position="relative">
            <CommitDot color={item.color} size="16px" glow />
            {/* branch arm */}
            <Box
              position="absolute"
              top="50%"
              transform="translateY(-50%)"
              h="2px"
              w="18px"
              bg={branchColor}
              {...(isLeft ? { right: "calc(100% + 4px)" } : { left: "calc(100% + 4px)" })}
            />
          </Box>
          {!isLast && <LineSeg color={color} position="fill" />}
        </Flex>

        {/* Right column */}
        <Box flex="1" display="flex" alignItems="center" justifyContent="flex-start" pl={0}>
          {!isLeft ? (
            <CardContent
              item={item}
              mutedText={mutedText}
              cardBg={cardBg}
              cardBorder={cardBorder}
              topAccent={topAccent}
              dateBg={dateBg}
              align="left"
            />
          ) : (
            <Text
              fontSize="sm"
              fontWeight={600}
              color={mutedText}
              fontFamily="mono"
              pl={4}
            >
              {item.date}
            </Text>
          )}
        </Box>
      </Flex>

      {/* Mobile — dots only, continuous line is rendered by parent */}
      <Flex display={{ base: "flex", md: "none" }} align="center">
        <Flex align="center" justify="center" w={{ base: "28px", sm: "36px" }} flexShrink={0}>
          <CommitDot color={item.color} size="12px" glow />
        </Flex>
        <Box flex="1" pl={{ base: 2, sm: 3 }} py={2} minW={0}>
          <CardContent
            item={item}
            mutedText={mutedText}
            cardBg={cardBg}
            cardBorder={cardBorder}
            topAccent={topAccent}
            dateBg={dateBg}
            align="left"
            isMobile
          />
        </Box>
      </Flex>
    </MotionBox>
  );
}

/* ─────────────────────── Card content ─────────────────────────── */

function CardContent({
  item,
  mutedText,
  cardBg,
  cardBorder,
  topAccent,
  dateBg,
  align,
  isMobile = false,
}) {
  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={cardBorder}
      borderTop="3px solid"
      borderTopColor={topAccent}
      borderRadius="lg"
      p={{ base: 3, sm: 4, md: 5 }}
      maxW={{ base: "100%", md: "400px" }}
      w="100%"
      ml={align === "right" ? "auto" : 0}
      mr={align === "left" ? "auto" : 0}
      position="relative"
      backdropFilter="blur(8px)"
      overflow="hidden"
      _hover={{
        borderColor: topAccent,
        boxShadow: `0 4px 24px -4px var(--chakra-colors-${item.color}-400)`,
        transform: "translateY(-2px)",
      }}
      transition="all 0.3s ease"
      role="group"
    >
      {/* Top row: type badge + date */}
      <Flex
        justify={isMobile ? "flex-start" : align === "right" ? "flex-end" : "flex-start"}
        align="center"
        gap={{ base: 1.5, sm: 2 }}
        mb={2}
        wrap="wrap"
      >
        <Badge
          colorScheme={item.color}
          fontSize={{ base: "3xs", sm: "2xs" }}
          textTransform="uppercase"
          letterSpacing="wider"
          px={{ base: 1.5, sm: 2 }}
          py={0.5}
          borderRadius="sm"
          fontWeight={700}
          flexShrink={0}
        >
          {item.type}
        </Badge>
        {isMobile && (
          <Text
            fontSize={{ base: "3xs", sm: "xs" }}
            color={mutedText}
            bg={dateBg}
            px={{ base: 1.5, sm: 2 }}
            py={0.5}
            borderRadius="sm"
            fontWeight={600}
            fontFamily="mono"
            whiteSpace="nowrap"
          >
            {item.date}
          </Text>
        )}
      </Flex>

      <Text
        fontWeight={700}
        fontSize={{ base: "xs", sm: "sm", md: "md" }}
        mb={1}
        lineHeight="short"
        noOfLines={isMobile ? 3 : undefined}
      >
        {item.title}
      </Text>
      <Text
        fontSize={{ base: "2xs", sm: "xs", md: "sm" }}
        color={mutedText}
        mb={{ base: 2, sm: 3 }}
        lineHeight="tall"
      >
        {item.description}
      </Text>

      {/* Tech badges */}
      <Flex
        wrap="wrap"
        gap={1}
        justify={isMobile ? "flex-start" : align === "right" ? "flex-end" : "flex-start"}
      >
        {item.badges.map((badge, i) => (
          <Badge
            key={i}
            colorScheme={badge.colorScheme}
            variant="subtle"
            fontSize={{ base: "3xs", sm: "2xs" }}
            px={{ base: 1, sm: 2 }}
            py={0.5}
            borderRadius="sm"
          >
            {badge.name}
          </Badge>
        ))}
      </Flex>

      {/* Git hash */}
      <Text
        fontSize="2xs"
        fontFamily="mono"
        color={mutedText}
        mt={2}
        textAlign={isMobile ? "left" : align}
        opacity={0.4}
        _groupHover={{ opacity: 0.7 }}
        transition="opacity 0.3s"
      >
        {item.hash}
      </Text>
    </Box>
  );
}

/* ───────────────────── Merge badge row ────────────────────────── */

function MergeRow({ label, mergeColor, mainColor, isFirst, isLast }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  const bg = useColorModeValue(`${mergeColor}.50`, "whiteAlpha.50");
  const border = useColorModeValue(`${mergeColor}.300`, `${mergeColor}.600`);
  const textColor = useColorModeValue(`${mergeColor}.700`, `${mergeColor}.200`);
  const iconColor = useColorModeValue(`${mergeColor}.500`, `${mergeColor}.400`);

  return (
    <MotionFlex
      ref={ref}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.4 }}
      direction="column"
      align="center"
      w="100%"
    >
      {/* Desktop */}
      <Flex display={{ base: "none", md: "flex" }} direction="column" align="center" w="100%">
        {!isFirst && <LineSeg color={mainColor} height="24px" />}
        <HStack
          bg={bg}
          border="1px solid"
          borderColor={border}
          px={5}
          py={2}
          borderRadius="full"
          spacing={2}
          zIndex={3}
        >
          <Icon as={FaCodeBranch} color={iconColor} boxSize={3} />
          <Text
            fontSize="xs"
            fontWeight={700}
            fontFamily="mono"
            letterSpacing="wide"
            color={textColor}
            textTransform="uppercase"
          >
            {label}
          </Text>
        </HStack>
        {!isLast && <LineSeg color={mainColor} height="24px" />}
      </Flex>

      {/* Mobile — dot only, continuous line is rendered by parent */}
      <Flex display={{ base: "flex", md: "none" }} align="center" w="100%">
        <Flex align="center" justify="center" w={{ base: "28px", sm: "36px" }} flexShrink={0}>
          <Box
            w={{ base: "8px", sm: "10px" }}
            h={{ base: "8px", sm: "10px" }}
            minW={{ base: "8px", sm: "10px" }}
            minH={{ base: "8px", sm: "10px" }}
            borderRadius="full"
            bg={useColorModeValue(`${mergeColor}.400`, `${mergeColor}.500`)}
            zIndex={3}
          />
        </Flex>
        <Flex flex="1" pl={{ base: 2, sm: 3 }} py={1.5} align="center" minW={0}>
          <HStack
            bg={bg}
            border="1px solid"
            borderColor={border}
            px={{ base: 2, sm: 3 }}
            py={1}
            borderRadius="lg"
            spacing={{ base: 1, sm: 2 }}
            maxW="100%"
          >
            <Icon as={FaCodeBranch} color={iconColor} boxSize={{ base: 2, sm: 2.5 }} flexShrink={0} />
            <Text
              fontSize={{ base: "3xs", sm: "2xs" }}
              fontWeight={700}
              fontFamily="mono"
              letterSpacing="wide"
              color={textColor}
              textTransform="uppercase"
              noOfLines={1}
            >
              {label}
            </Text>
          </HStack>
        </Flex>
      </Flex>
    </MotionFlex>
  );
}

/* ────────────────── HEAD terminal node ─────────────────────────── */

function HeadNode({ color }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  const bg = useColorModeValue(`${color}.500`, `${color}.400`);
  const ringBg = useColorModeValue(`${color}.100`, `${color}.900`);
  const labelColor = useColorModeValue("gray.500", "gray.400");

  return (
    <MotionFlex
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5 }}
      direction="column"
      align="center"
      w="100%"
    >
      {/* Desktop */}
      <Flex display={{ base: "none", md: "flex" }} direction="column" align="center">
        <LineSeg color={color} height="32px" />
        <Box position="relative">
          <Box
            w="22px"
            h="22px"
            borderRadius="full"
            bg={bg}
            boxShadow={`0 0 14px 4px var(--chakra-colors-${color}-400)`}
            zIndex={3}
          />
          <Box
            position="absolute"
            inset="-6px"
            borderRadius="full"
            bg={ringBg}
            opacity={0.3}
            zIndex={2}
          />
          {/* pulse ring */}
          <Box
            as={motion.div}
            position="absolute"
            inset="-10px"
            borderRadius="full"
            border="2px solid"
            borderColor={bg}
            zIndex={1}
            animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </Box>
        <Text
          fontSize="xs"
          fontFamily="mono"
          fontWeight={700}
          color={labelColor}
          mt={3}
          letterSpacing="wide"
        >
          HEAD → main
        </Text>
      </Flex>

      {/* Mobile — dot only, continuous line is rendered by parent */}
      <Flex display={{ base: "flex", md: "none" }} align="center" w="100%">
        <Flex align="center" justify="center" w={{ base: "28px", sm: "36px" }} flexShrink={0}>
          <Box
            w={{ base: "14px", sm: "18px" }}
            h={{ base: "14px", sm: "18px" }}
            minW={{ base: "14px", sm: "18px" }}
            borderRadius="full"
            bg={bg}
            boxShadow={`0 0 10px 3px var(--chakra-colors-${color}-400)`}
            zIndex={3}
          />
        </Flex>
        <Flex flex="1" pl={{ base: 2, sm: 3 }} py={2} align="center">
          <Text
            fontSize={{ base: "2xs", sm: "xs" }}
            fontFamily="mono"
            fontWeight={700}
            color={labelColor}
            letterSpacing="wide"
          >
            HEAD → main
          </Text>
        </Flex>
      </Flex>
    </MotionFlex>
  );
}

/* ═══════════════════════ Main page ═══════════════════════════════ */

export default function JourneyPage({ color }) {
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const mobileLineBg = useColorModeValue(`${color}.300`, `${color}.700`);

  const { heading, subtitle, milestones } = t.journey;

  useEffect(() => {
    document.title = t.journey.pageTitle;
  }, []);

  // Separate non-merge items to track alternating index for left/right cards
  let cardIndex = 0;

  return (
    <Box bg={pageBg} minH="100vh" pt={24} pb={20}>
      <Container maxW="container.lg" px={{ base: 3, sm: 4, md: 8 }}>
        {/* Header */}
        <VStack spacing={{ base: 3, sm: 4 }} mb={{ base: 8, sm: 10, md: 14 }} textAlign="center">
          <Badge
            colorScheme={color}
            fontSize={{ base: "3xs", sm: "xs" }}
            px={{ base: 2, sm: 3 }}
            py={1}
            borderRadius="sm"
            fontFamily="mono"
            letterSpacing="wider"
            maxW="100%"
          >
            <Text as="span" noOfLines={1}>
              $ git log --graph --oneline
            </Text>
          </Badge>
          <Heading
            size={{ base: "lg", sm: "xl", md: "2xl" }}
            fontWeight={800}
          >
            {heading}
          </Heading>
          <Text
            fontSize={{ base: "sm", sm: "md", md: "lg" }}
            color={subtitleColor}
            maxW="560px"
            lineHeight="tall"
            px={{ base: 2, sm: 0 }}
          >
            {subtitle}
          </Text>
        </VStack>

        {/* Timeline */}
        <Box position="relative">
          {/* Continuous vertical line for mobile — one unbroken stroke */}
          <Box
            display={{ base: "block", md: "none" }}
            position="absolute"
            top={0}
            bottom={0}
            left={{ base: "13px", sm: "17px" }}
            w="2px"
            bg={mobileLineBg}
            borderRadius="full"
            zIndex={1}
          />

          <VStack spacing={0} align="stretch" position="relative" zIndex={2}>
            {milestones.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === milestones.length - 1;

              if (item.isMerge) {
                return (
                  <MergeRow
                    key={index}
                    label={item.label}
                    mergeColor={item.color || color}
                    mainColor={color}
                    isFirst={isFirst}
                    isLast={isLast}
                  />
                );
              }

              const isLeft = cardIndex % 2 === 0;
              cardIndex++;

              return (
                <BranchCard
                  key={index}
                  item={item}
                  isLeft={isLeft}
                  color={color}
                  isFirst={isFirst}
                  isLast={isLast}
                />
              );
            })}

            {/* HEAD */}
            <HeadNode color={color} />
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}
